import {
  homeLoanEnquiryInitialState,
  aboutYouInitialState,
  financialDetailsInitialState,
} from '../store/initialState';
import type {
    ApplicantState,
    CommonState,
    IEnquiryAPIRequesterDetails,
    IGeneralEnquiryAPI,
    IHomeLoanEnquiryAPI,
    IPersonalLoanAPI,
    IHardshipAPI,
    IFinancialPlanningAPI,
} from '../store/types';
import type { IPersonalDetails } from './personalDetailsType';

export const mapToPersonalDetailsDto = (
  personalDetails: ApplicantState,
  currentApplicantIndex: number,
  commonDetails: CommonState,
): IPersonalDetails => {
  const personalDetailsDto: IPersonalDetails = {
    currentApplicantIndex: currentApplicantIndex,
    utmCampaignCode: commonDetails.utmCampaignCode,
    aboutYou: {
      ...personalDetails.aboutYou,
    },
  };

  return personalDetailsDto;
};

const mapApplicationType = (applicationType): string => {
    switch (applicationType) {
    case "NewBuild":
        return "Construction";

    case "ManageExistingLoan":
        return "MaintainExistingLoan";

    default:
        return applicationType;
    }
};

export const mapToGeneralEnquiryDto = (
  personalDetails: ApplicantState, clientDetails: string | null
): IGeneralEnquiryAPI => {

  let callerDetailsDto: IEnquiryAPIRequesterDetails = {
      ipAddress: !clientDetails ? "123.123.123.123" : clientDetails,
      userAgentString: window.navigator.userAgent,
      deviceId: "",
      sessionId: "",
  };

  let generalEnquiryDto: IGeneralEnquiryAPI = {
    correlationId: "",
    originationBrand: "NP",
    originationSystem: "PublicWebsite",
    firstName: personalDetails.aboutYou.firstName.trim(),
    lastName: personalDetails.aboutYou.lastName.trim(),
    emailAddress: personalDetails.aboutYou.emailAddress.trim(),
    mobileNumber: personalDetails.aboutYou.mobileNumber.trim(),
    preferredContactMethod: personalDetails.aboutYou.contactMethodGeneral,
    isExistingCustomer: personalDetails.aboutYou.isExistingCustomer,
    generalMessage: personalDetails.generalEnquiryDetails.generalMessage,
    privacyConsent: true,
    callerDetails: callerDetailsDto
  };

  return generalEnquiryDto;
};

export const mapToHomeLoanEnquiryDto = (
    personalDetails: ApplicantState, clientDetails: string | null
): IHomeLoanEnquiryAPI => {

    let callerDetailsDto: IEnquiryAPIRequesterDetails = {
        ipAddress: !clientDetails ? "123.123.123.123" : clientDetails,
        userAgentString: window.navigator.userAgent,
        deviceId: "",
        sessionId: "",
    };

    let homeLoanEnquiryDto: IHomeLoanEnquiryAPI = {
        correlationId: "",
        originationBrand: "NPBS", // NPBS or GB
        originationSystem: "PublicWebsite",
        loanPurpose: mapApplicationType(personalDetails.homeLoanEnquiryDetails.ApplicationType),  // Purchase,Refinance,,Construction,BridgingLoanVacantLand,IncreaseExistingLoan,MaintainExistingLoan
        loanType: personalDetails.homeLoanEnquiryDetails.PropertyLoanType === "" ? "OwnerOccupied" : personalDetails.homeLoanEnquiryDetails.PropertyLoanType ,
        //personalDetails.homeLoanEnquiryDetails.IsOwnerOccupied ? "OwnerOccupied" : "Investment", //personalDetails.homeLoanEnquiryType, // OwnerOccupied or Investment
        loanAmount: +personalDetails.financialDetails.homeLoanBorrowingAmount,
        additionalFunds: personalDetails?.financialDetails?.homeLoanAdditionalFunds === undefined ?
            0 : +personalDetails?.financialDetails?.homeLoanAdditionalFunds,
        //append utm= prefix if campaign code exists
        comments: personalDetails.homeLoanEnquiryDetails.CampaignCode.trim(),
        givenName: personalDetails.aboutYou.firstName.trim(),
        familyName: personalDetails.aboutYou.lastName.trim(),
        homePhone: "",
        workPhone: "",
        mobilePhone: personalDetails.aboutYou.mobileNumber.trim(),
        email: personalDetails.aboutYou.emailAddress.trim(),
        employmentStatus: personalDetails.aboutYou.employmentStatus, //NA,Salary,SelfEmployed
        isExistingMember: personalDetails.homeLoanEnquiryDetails.IsExistingMember,
        privacyConsent: true,
        preferredContactMethod: personalDetails.aboutYou.contactMethod, // Phone, Email
        callerDetails: callerDetailsDto
    };

    switch (homeLoanEnquiryDto.loanPurpose) {
        case "IncreaseExistingLoan":
        case "MaintainExistingLoan":
            homeLoanEnquiryDto.loanType = "OwnerOccupied";
            homeLoanEnquiryDto.employmentStatus = "NA";
            homeLoanEnquiryDto.isExistingMember = true;
            homeLoanEnquiryDto.privacyConsent = true;
            homeLoanEnquiryDto.preferredContactMethod = "Phone";

            if (homeLoanEnquiryDto.loanPurpose === "MaintainExistingLoan") {
                homeLoanEnquiryDto.loanAmount = 1;
            }
            break;
    }

    return homeLoanEnquiryDto;
};

export const mapToPersonalLoanDto = (
  personalDetails: ApplicantState, clientDetails: string | null
): IPersonalLoanAPI => {

  let callerDetailsDto: IEnquiryAPIRequesterDetails = {
      ipAddress: !clientDetails ? "123.123.123.123" : clientDetails,
      userAgentString: window.navigator.userAgent,
      deviceId: "",
      sessionId: "",
  };

  let generalEnquiryDto: IPersonalLoanAPI = {
    correlationId: "",
    originationBrand: "NP",
    originationSystem: "PublicWebsite",
    firstName: personalDetails.aboutYou.firstName.trim(),
    lastName: personalDetails.aboutYou.lastName.trim(),
    emailAddress: personalDetails.aboutYou.emailAddress.trim(),
    mobileNumber: personalDetails.aboutYou.mobileNumber.trim(),
    isExistingCustomer: personalDetails.aboutYou.isExistingCustomer,
    customerId: personalDetails.aboutYou.customerId,
    residentialAddress: personalDetails.aboutYou.residentialAddress,
    loanAmount: +personalDetails.financialDetails.personalLoanBorrowingAmount,
    loanPurpose: personalDetails.financialDetails.personalLoanBorrowingPurpose,
    housingSituation: personalDetails.financialDetails.personalLoanHousingSituation,
    hasExistingHomeLoan: Boolean(personalDetails.financialDetails.personalLoanHasExistingHomeLoan),
    grossSalary: +personalDetails.aboutYou.grossSalary,
    employmentStatus: personalDetails.aboutYou.employmentStatus,
    privacyConsent: true,
    callerDetails: callerDetailsDto
  };

  return generalEnquiryDto;
};

export const mapToHardshipDto = (
  personalDetails: ApplicantState, clientDetails: string | null
): IHardshipAPI => {

  let callerDetailsDto: IEnquiryAPIRequesterDetails = {
      ipAddress: !clientDetails ? "123.123.123.123" : clientDetails,
      userAgentString: window.navigator.userAgent,
      deviceId: "",
      sessionId: "",
  };

  let generalEnquiryDto: IHardshipAPI = {
    correlationId: "",
    originationBrand: "NP",
    originationSystem: "PublicWebsite",
    firstName: personalDetails.aboutYou.firstName.trim(),
    lastName: personalDetails.aboutYou.lastName.trim(),
    emailAddress: personalDetails.aboutYou.emailAddress.trim(),
    mobileNumber: personalDetails.aboutYou.mobileNumber.trim(),
    generalMessage: personalDetails.generalEnquiryDetails.generalMessage,
    privacyConsent: true,
    callerDetails: callerDetailsDto
  };

  return generalEnquiryDto;
};

export const mapToFinancialPlanningDto = (
  personalDetails: ApplicantState, clientDetails: string | null
): IFinancialPlanningAPI => {

  let callerDetailsDto: IEnquiryAPIRequesterDetails = {
      ipAddress: !clientDetails ? "123.123.123.123" : clientDetails,
      userAgentString: window.navigator.userAgent,
      deviceId: "",
      sessionId: "",
  };

  let financialPlanningDto: IFinancialPlanningAPI = {
    correlationId: "",
    originationBrand: "NP",
    originationSystem: "PublicWebsite",
    firstName: personalDetails.aboutYou.firstName.trim(),
    lastName: personalDetails.aboutYou.lastName.trim(),
    emailAddress: personalDetails.aboutYou.emailAddress.trim(),
    mobileNumber: personalDetails.aboutYou.mobileNumber.trim(),
    preferredContactMethod: personalDetails.aboutYou.contactMethodGeneral,
    isExistingCustomer: personalDetails.aboutYou.isExistingCustomer,
    customerId: personalDetails.aboutYou.customerId,
    residentialAddress: personalDetails.aboutYou.residentialAddress,
    ageRange: personalDetails.aboutYou.ageRange,
    enquiryType: personalDetails.homeLoanEnquiryDetails.EnquiryType,
    generalMessage: personalDetails.generalEnquiryDetails.generalMessage,
    privacyConsent: true,
    callerDetails: callerDetailsDto
  };

  return financialPlanningDto;
};

export const mapRetrievalToRedux = (retrievalData: any[]): ApplicantState[] => {
  let applicantsData = [] as ApplicantState[];

  retrievalData.forEach((applicant) => {
    const applicantData: ApplicantState = {
      applicantId: applicant.applicantId,
      customerId: applicant.customerId,
      aboutYou: {
        ...aboutYouInitialState,
        ...applicant.aboutYou,
      },
      homeLoanEnquiryDetails: {
        ...homeLoanEnquiryInitialState,
      },

      financialDetails: {
        ...financialDetailsInitialState,
        ...applicant.financialDetails,
      },
        generalEnquiryDetails: {
            ...applicant.generalEnquiryDetails,
        }
    };

    applicantsData.push(applicantData);
  });

  return applicantsData;
};
